/**
 * One key: tapped it does the thing on the screen, held it offers the rest.
 *
 * The hold is where the rarer verbs live — parking a cart, taking a chair back
 * off a table, the two options on a deal you did not take — and it is the same
 * key so there is nothing to remember. Nothing is unreachable and nothing is
 * hidden: the fan is exactly `rest`, which is exactly everything the router
 * found here and did not put first.
 *
 * Released over an entry, that entry runs. Released over nothing, nothing
 * happens — which is how you back out of it.
 *
 * This is the state machine and nothing else. It decides, frame by frame, which
 * one thing the key did — and it is the one place that decides, because every
 * bug this key has ever had was one press reaching two handlers: a number that
 * chose a row and then a release that chose another, a tap that ran the top
 * option and a hold that ran it again. `frame` returns exactly one outcome and
 * the caller performs it; nothing here performs anything or draws anything.
 *
 * Outcomes:
 *
 *   picked      a number chose a row while the fan was open: perform `option`
 *   released    the key came up with the fan open: perform `option` (may be null)
 *   navigating  the fan is open and the key still held: draw `options` at `at`
 *   spent       the key coming up after a number already chose: draw nothing
 *   tapped      a tap: perform `option`, the top of the list
 *   holding     held, and counting towards the fan: nothing to draw yet
 *   opened      the hold reached the threshold: draw `options` at 0
 *   idle        none of the above: the caller draws the prompt
 */

/** Seconds of holding the key before the fan opens. */
export const FAN_AFTER = 0.22;

export function createActionKey() {
  let fanFor = 0;
  let fanAt = 0;
  let fanning = false;
  /** A number that chose a row, queued until the frame reads it. */
  let fanPick = null;
  /** Set when a number has already chosen, so letting go does not choose again. */
  let fanSpent = false;

  /**
   * One frame of the key.
   *
   * `held`, `tapped`, `up` and `down` are this frame's input; `here` is what the
   * router offers, `{ top, rest }`.
   */
  function frame({ held, tapped, up = false, down = false, dt = 1 / 60 }, here) {
    if (fanning) {
      const options = [here.top, ...here.rest].filter(Boolean);

      /*
       * A number picks its row and ends it there, without waiting for the key
       * to come up. Which is the whole point of having numbers: the hold is how
       * you open the menu and it should not also be how you commit to it.
       */
      if (fanPick !== null) {
        const picked = options[fanPick] ?? null;
        fanPick = null;
        if (picked) {
          fanning = false;
          fanSpent = true;
          fanFor = 0;
          return { outcome: 'picked', option: picked };
        }
      }

      if (!held) {
        fanning = false;
        return { outcome: 'released', option: options[fanAt] ?? null };
      }
      // Up and down the list with the same keys that move you, because there
      // is nothing else left to bind and a fan is not a place you walk. The
      // numbers are the other way in, for anybody who can see the row they want.
      if (up) fanAt = Math.max(0, fanAt - 1);
      if (down) fanAt = Math.min(options.length - 1, fanAt + 1);
      return { outcome: 'navigating', options, at: fanAt };
    }

    /*
     * The key coming up after a number already chose.
     *
     * Without this the release runs the highlighted row as well, so picking [3]
     * does the third thing and then, a frame later, the first one too.
     */
    if (fanSpent) {
      if (!held) fanSpent = false;
      return { outcome: 'spent' };
    }

    if (tapped) {
      fanFor = 0;
      return { outcome: 'tapped', option: here.top };
    }

    // Held without a tap this frame means the tap already went; a hold that
    // reaches the threshold opens the fan instead, provided there is a rest to
    // fan out. One option is not a menu.
    if (held && (here.rest.length || here.top)) {
      fanFor += dt;
      if (fanFor >= FAN_AFTER) {
        fanning = true;
        fanAt = 0;
        fanPick = null;
        return { outcome: 'opened', options: [here.top, ...here.rest].filter(Boolean), at: 0 };
      }
      return { outcome: 'holding' };
    }
    fanFor = 0;
    return { outcome: 'idle' };
  }

  return {
    frame,
    /** A number key, from the keyboard or from a script. Only counts while the fan is open. */
    pick(index) {
      if (!fanning) return false;
      fanPick = index;
      return true;
    },
    /**
     * Points the fan at a row directly: the pad's wheel, where the left stick's
     * angle is the row. Only while the fan is open; the release still commits.
     */
    aim(index) {
      if (!fanning || !Number.isInteger(index) || index < 0) return false;
      fanAt = index;
      return true;
    },
    /**
     * Backed out of on purpose (B on a pad): the fan goes, and the key still
     * held does nothing until it comes up, so the wheel does not open again.
     */
    dismiss() {
      if (!fanning) return false;
      fanning = false;
      fanPick = null;
      fanSpent = true;
      fanFor = 0;
      return true;
    },
    /** A panel came up over the fan: it goes, and nothing it was holding carries over. */
    cancel() {
      if (!fanning && fanPick === null) return false;
      fanning = false;
      fanPick = null;
      fanSpent = false;
      return true;
    },
    get fanning() {
      return fanning;
    },
    get at() {
      return fanAt;
    },
  };
}
